import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Camera,
  Copy,
  Github,
  Megaphone,
  MessageCircle,
  Smartphone,
  Trees,
} from "lucide-react";

const STEPS = [
  {
    icon: Smartphone,
    title: "Open EcoRoute on your phone",
    desc: "Leave the desktop tab behind — this one's made for pockets.",
  },
  {
    icon: Trees,
    title: "Head to the nearest park or trail",
    desc: "Ten minutes away counts. The trees don't judge distance.",
  },
  {
    icon: Camera,
    title: "Snap a selfie with nature in frame",
    desc: "You and leaves in the same photo. That's the whole brief.",
  },
  {
    icon: Megaphone,
    title: "Post it to claim your points",
    desc: "Share with #TouchGrass and #Hacktoberfest to score your bonus.",
  },
];

const SELFIE_IMG =
  "https://images.unsplash.com/photo-1758506245239-cf2c3eb41e4e?crop=entropy&cs=srgb&fm=jpg&q=85";

const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const ease = [0.22, 1, 0.36, 1];

export default function BonusChallenge() {
  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(
        "I just went outside with EcoRoute — Touch Grass. Escape your screen. Explore nature. #TouchGrass #Hacktoberfest"
      );
      toast.success("Challenge text copied. Now go post it.");
    } catch {
      toast.error("Couldn't copy — your browser said no.");
    }
  };

  return (
    <section id="about" className="scroll-mt-20 px-5 py-24 md:px-12">
      <motion.div
        data-testid="bonus-card"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.8, ease }}
        className="relative mx-auto max-w-6xl overflow-hidden rounded-[2rem] border border-clay/30 bg-gradient-to-br from-forest-800 via-forest-900 to-forest-700/60 p-7 md:p-12"
      >
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-clay/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-moss/10 blur-3xl" />

        <div className="relative grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-clay">
              Hackathon Bonus Point Challenge
            </p>
            <h2 className="mt-4 font-display text-3xl tracking-tight text-cream sm:text-4xl lg:text-5xl">
              Take us outside.
              <br />
              <span className="italic text-clay">Literally.</span>
            </h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-forest-dim md:text-base">
              Four steps between you and bonus points. No code required — just shoes.
            </p>

            <div className="mt-9 grid gap-4 sm:grid-cols-2">
              {STEPS.map((s, i) => (
                <motion.div
                  key={i}
                  data-testid={`bonus-step-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease }}
                  className="rounded-2xl border border-forest-600/50 bg-forest-900/50 p-5 transition-colors hover:border-moss/50"
                >
                  <div className="flex items-center justify-between">
                    <s.icon className="h-5 w-5 text-moss" />
                    <span className="font-mono text-xs text-clay">0{i + 1}</span>
                  </div>
                  <p className="mt-4 font-medium leading-snug text-cream">{s.title}</p>
                  <p className="mt-2 text-xs leading-relaxed text-forest-muted">{s.desc}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-9 flex flex-wrap gap-3">
              <a
                data-testid="share-x"
                href="https://twitter.com/intent/tweet?text=I%20just%20went%20outside%20with%20EcoRoute%20%E2%80%94%20Touch%20Grass.%20%23TouchGrass%20%23Hacktoberfest"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-cream px-5 py-2.5 text-sm font-semibold text-forest-950 transition-transform hover:scale-[1.03]"
              >
                <XIcon className="h-4 w-4" /> Share on X
              </a>
              <a
                data-testid="share-github"
                href="https://hacktoberfest.com"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-forest-500/60 px-5 py-2.5 text-sm text-cream/90 transition-colors hover:border-moss/70 hover:text-moss"
              >
                <Github className="h-4 w-4" /> Hacktoberfest
              </a>
              <a
                data-testid="share-discord"
                href="https://discord.com"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-forest-500/60 px-5 py-2.5 text-sm text-cream/90 transition-colors hover:border-moss/70 hover:text-moss"
              >
                <MessageCircle className="h-4 w-4" /> Discord
              </a>
              <button
                data-testid="bonus-copy-button"
                onClick={copyText}
                className="inline-flex items-center gap-2 rounded-full border border-clay/50 px-5 py-2.5 text-sm text-clay transition-colors hover:bg-clay/10"
              >
                <Copy className="h-4 w-4" /> Copy challenge
              </button>
            </div>
          </div>

          <div className="relative">
            <div className="overflow-hidden rounded-[1.75rem] border border-clay/25 shadow-[0_30px_70px_rgba(0,0,0,0.4)]">
              <img
                src={SELFIE_IMG}
                alt="Snapping a selfie with an autumn forest"
                className="h-[420px] w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
            <div className="absolute -bottom-5 left-6 rounded-full border border-clay/40 bg-forest-950/95 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] text-clay backdrop-blur">
              Exhibit A — acceptable proof of grass contact
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
